import type { CascadingSelectValuePair } from '@atlassian/jira-issue-field-cascading-select-editview-full/src/ui/cascading-select/types.tsx';
import { CASCADING_SELECT_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useCascadingSelectField } from '../../../cascading-select/index.tsx';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component/index.tsx';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler/index.tsx';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasRichFormat,
} from '../../../clipboard-handler/utils.tsx';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteHandler,
	PasteMetadata,
} from '../../../types.tsx';
import type { CascadingSelectFieldValue, TransformedCascadingSelectField } from '../types.tsx';

const isCascadingSelectOption = (fieldValue: unknown): fieldValue is CascadingSelectFieldValue =>
	fieldValue != null && typeof fieldValue === 'object' && 'parentOptionValue' in fieldValue;

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): CascadingSelectFieldValue | null => {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, [CASCADING_SELECT_CF_TYPE])
	) {
		return null;
	}

	if (!isCascadingSelectOption(metadata.fieldValue)) {
		return null;
	}

	const { parentOptionValue, childOptionValue } = metadata.fieldValue;

	return { parentOptionValue, childOptionValue };
};

export const getStringValue = (fieldValue: CascadingSelectValuePair | null) => {
	if (fieldValue && fieldValue.parentOptionValue) {
		if (fieldValue.childOptionValue) {
			return `${fieldValue.parentOptionValue.value} - ${fieldValue.childOptionValue.value}`;
		}
		return `${fieldValue.parentOptionValue.value}`;
	}
	return '';
};

function assertCascadingSelectField(
	field: ListIssueFieldTransformed,
): asserts field is TransformedCascadingSelectField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (field.type !== CASCADING_SELECT_CF_TYPE) {
		throw new Error('Field type is not supported');
	}
}

const useCascadingSelectClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertCascadingSelectField(field);

	const [{ value }, { saveValue }] = useCascadingSelectField({
		fieldAriId: field.id,
		issueKey: issue.key,
		fieldKey: field.fieldId,
		initialValue: isCascadingSelectOption(field.value) ? field.value : null,
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: getStringValue(value),
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const CascadingSelectClipboardHelper = createClipboardHelperComponent(
	useCascadingSelectClipboardHandler,
);

export const cascadingSelectClipboardHandler = {
	[CASCADING_SELECT_CF_TYPE]: CascadingSelectClipboardHelper,
};
